import type { AxiosInstance } from "axios";

/**
 * Agency client invites (Flow 2) — mirrors the API's `store-invites` module (apps/web cannot bundle
 * server code, the same trade lib/team.ts makes).
 */
export type StoreInviteStatus = "active" | "used" | "expired" | "revoked";

export interface StoreInvite {
  readonly id: string;
  readonly clientName: string;
  readonly status: StoreInviteStatus;
  readonly createdAt: string;
  readonly expiresAt: string;
  readonly usedAt: string | null;
  readonly createdBy: string | null;
  readonly store: { readonly id: string; readonly displayName: string; readonly shopDomain: string } | null;
}

export interface StoreInvites {
  /** False for a brand, or a member who does not manage the agency: nothing to offer. */
  readonly available: boolean;
  readonly orgName: string | null;
  readonly invites: readonly StoreInvite[];
}

export interface CreatedInvite {
  readonly id: string;
  /** Shown ONCE — the API keeps only a hash of its secret. */
  readonly url: string;
  readonly expiresAt: string;
}

export interface PublicInvite {
  readonly agencyName: string;
  readonly clientName: string;
  readonly status: StoreInviteStatus;
  readonly expiresAt: string;
}

export async function fetchStoreInvites(client: AxiosInstance): Promise<StoreInvites> {
  const response = await client.get<{ success: boolean; data: StoreInvites }>("/api/v2/store-invites");
  return response.data.data;
}

export async function createStoreInvite(client: AxiosInstance, clientName: string): Promise<CreatedInvite> {
  const response = await client.post<{ success: boolean; data: CreatedInvite }>("/api/v2/store-invites", { clientName });
  return response.data.data;
}

export async function revokeStoreInvite(client: AxiosInstance, id: string): Promise<void> {
  await client.post(`/api/v2/store-invites/${encodeURIComponent(id)}/revoke`);
}

export async function fetchPublicInvite(client: AxiosInstance, token: string): Promise<PublicInvite> {
  const response = await client.get<{ success: boolean; data: PublicInvite }>(`/public/store-invites/${encodeURIComponent(token)}`);
  return response.data.data;
}

export async function startInvitedInstall(client: AxiosInstance, token: string, shop: string): Promise<string> {
  const response = await client.post<{ success: boolean; data: { installUrl: string } }>(
    `/public/store-invites/${encodeURIComponent(token)}/install`,
    { shop },
  );
  return response.data.data.installUrl;
}

/** The badge for a link's state. */
export const INVITE_STATUS_LABEL: Record<StoreInviteStatus, string> = {
  active: "Waiting for install",
  used: "Installed",
  expired: "Expired",
  revoked: "Revoked",
};

/** "29 Oct 2026" in the viewer's zone. */
export function formatInviteDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}
